import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { selectedCompanyHasProfile } from "../utils/activityProfiles";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message as antdMessage,
} from "antd";
import {
  CheckCircleOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  GlobalOutlined,
  PictureOutlined,
  SettingOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  PrinterOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  createRestaurantDigitalMenuTable,
  deactivateRestaurantDigitalMenuTable,
  getRestaurantDigitalMenu,
  saveRestaurantDigitalMenu,
  updateRestaurantDigitalMenuTable,
} from "../GestionProductions/api";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import API_CONFIG from "../config/api";
import RowActionsMenu from "../components/RowActionsMenu";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import useBusinessResource from "../hooks/useBusinessResource";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";
import RestaurantDigitalMenuPreview from "./RestaurantDigitalMenuPreview";
import "./RestaurantDigitalMenu.css";

const { Text, Title } = Typography;
const { TextArea } = Input;

type RestaurantMenu = {
  id?: number;
  company_id?: number;
  warehouse_id?: number;
  menu_name?: string;
  menu_slug?: string;
  description?: string | null;
  logo_url?: string | null;
  theme_config?: {
    primary_color?: string;
    accent_color?: string;
    secondary_color?: string;
  } | null;
  is_active?: boolean;
  allow_self_service_orders?: boolean;
  auto_send_to_kitchen?: boolean;
  require_table_token?: boolean;
  require_customer_phone?: boolean;
  default_service_mode?: "dine_in" | "takeaway" | "delivery";
};

type RestaurantTable = {
  id: number;
  warehouse_id: number;
  label: string;
  public_token: string;
  seats?: number | null;
  display_order?: number;
  notes?: string | null;
  is_active: boolean;
};

type QrPrintCard = {
  key: string;
  title: string;
  subtitle: string;
  url: string;
  qr: string;
};

type MenuFormValues = {
  menu_name: string;
  menu_slug?: string;
  description?: string;
  logo_url?: string;
  is_active?: boolean;
  allow_self_service_orders?: boolean;
  auto_send_to_kitchen?: boolean;
  require_table_token?: boolean;
  require_customer_phone?: boolean;
  default_service_mode?: "dine_in" | "takeaway" | "delivery";
  primary_color?: string;
  accent_color?: string;
  secondary_color?: string;
};

type TableFormValues = {
  label: string;
  seats?: number;
  display_order?: number;
  notes?: string;
  is_active?: boolean;
};

const MENU_DIGITAL_VIEW_PERMISSION =
  "Gestion Commerciale.Restauration.MenuDigital.view";
const MENU_DIGITAL_EDIT_PERMISSION =
  "Gestion Commerciale.Restauration.MenuDigital.edit";

const SERVICE_MODE_OPTIONS = [
  { value: "dine_in", label: "Sur place" },
  { value: "takeaway", label: "À emporter" },
  { value: "delivery", label: "Livraison" },
];

const DEFAULT_THEME = {
  primary_color: "#173f5f",
  accent_color: "#be4349",
  secondary_color: "#f5f8fb",
};
const QR_PRINT_SIGNATURE =
  "Powered by ELSA GESTION | www.elsagestion.com | +226 64 98 90 99";

const buildPublicBaseUrl = () => {
  const configured = process.env.REACT_APP_PUBLIC_BASE_URL;
  if (configured) {
    return configured.replace(/\/+$/, "");
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return API_CONFIG.BASE_URL.replace(/\/+$/, "");
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 160);

const copyToClipboard = async (value: string, successMessage: string, message: Pick<typeof antdMessage, "success" | "error">) => {
  try {
    await navigator.clipboard.writeText(value);
    message.success(successMessage);
  } catch (error) {
    message.error("Copie impossible depuis ce navigateur.");
  }
};

const generateQrDataUrl = (value: string) =>
  QRCode.toDataURL(value, {
    width: 360,
    margin: 2,
    errorCorrectionLevel: "M",
    color: {
      dark: "#142033",
      light: "#ffffff",
    },
  });

const escapeHtml = (value: string | number | null | undefined) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const menuFormValues = (menu: RestaurantMenu | null, companyName?: string): MenuFormValues => ({
  menu_name: menu?.menu_name || companyName || "Menu digital",
  menu_slug: menu?.menu_slug || slugify(companyName || "menu"),
  description: menu?.description || "",
  logo_url: menu?.logo_url || "",
  is_active: menu?.is_active ?? false,
  allow_self_service_orders: menu?.allow_self_service_orders ?? true,
  auto_send_to_kitchen: menu?.auto_send_to_kitchen ?? false,
  require_table_token: menu?.require_table_token ?? true,
  require_customer_phone: menu?.require_customer_phone ?? false,
  default_service_mode: menu?.default_service_mode || "dine_in",
  primary_color: menu?.theme_config?.primary_color || DEFAULT_THEME.primary_color,
  accent_color: menu?.theme_config?.accent_color || DEFAULT_THEME.accent_color,
  secondary_color: menu?.theme_config?.secondary_color || DEFAULT_THEME.secondary_color,
});

const tableSnapshot = (table: RestaurantTable) => JSON.stringify([
  table.label, table.seats ?? null, table.display_order ?? 0, table.notes || "", Boolean(table.is_active),
]);

const MenuColorInput = ({ value, onChange, id, label }: {
  value?: string; onChange?: (value: string) => void; id?: string; label: string;
}) => <div className="restaurant-digital-menu__color-input">
  <Input id={id} type="color" value={/^#[0-9a-f]{6}$/i.test(value || "") ? value : "#173f5f"}
    aria-label={label} onChange={(event) => onChange?.(event.target.value)} />
  <Input value={value || ""} aria-label={`${label} (hexadécimal)`} maxLength={7}
    onChange={(event) => onChange?.(event.target.value)} />
</div>;

const RestaurantDigitalMenu: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const [form] = Form.useForm<MenuFormValues>();
  const [tableForm] = Form.useForm<TableFormValues>();
  const [message, messageContextHolder] = antdMessage.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("presentation");
  const [formScope, setFormScope] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [remoteChanged, setRemoteChanged] = useState(false);
  const dirtyFields = useRef(new Set<string>());
  const baseline = useRef<MenuFormValues | null>(null);
  const writeInProgress = useRef(false);
  const [saving, setSaving] = useState(false);
  const [menuQr, setMenuQr] = useState<string | null>(null);
  const [tableQrMap, setTableQrMap] = useState<Record<number, string>>({});
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null);
  const [tableSaving, setTableSaving] = useState(false);
  const [tableConflict, setTableConflict] = useState(false);
  const [qrPrintModalOpen, setQrPrintModalOpen] = useState(false);
  const [selectedQrPrintKeys, setSelectedQrPrintKeys] = useState<string[]>([]);
  const [qrPrinting, setQrPrinting] = useState(false);
  const draftValues = Form.useWatch([], { form, preserve: true }) as MenuFormValues | undefined;
  const watchedAllowOrders = draftValues?.allow_self_service_orders;
  const watchedRequireTableToken = draftValues?.require_table_token;
  const watchedDefaultServiceMode = draftValues?.default_service_mode;

  const activeWarehouseId = useMemo(() => {
    const selected = Number(selectedWarehouse);
    if (Number.isFinite(selected) && selected > 0) {
      return selected;
    }

    const firstWarehouse = user?.activeWarehouses?.[0]?.id;
    return firstWarehouse ? Number(firstWarehouse) : null;
  }, [selectedWarehouse, user?.activeWarehouses]);

  const activeCompanyId = useMemo(() => {
    const selected = Number(selectedCompany);
    if (Number.isFinite(selected) && selected > 0) {
      return selected;
    }

    const userCompanyId = Number(user?.company_id || user?.company?.id);
    return Number.isFinite(userCompanyId) && userCompanyId > 0
      ? userCompanyId
      : null;
  }, [selectedCompany, user?.company?.id, user?.company_id]);

  const isSuperAdmin = Boolean(user?.is_superadmin || user?.role === "SysAdmin");
  const isRestaurantCompany =
    isSuperAdmin ||
    selectedCompanyHasProfile(user, selectedCompany, "restaurant");
  const canView = hasPermission(MENU_DIGITAL_VIEW_PERMISSION);
  const canEdit = hasPermission(MENU_DIGITAL_EDIT_PERMISSION);
  const scope = { userId: user?.id, companyId: activeCompanyId, warehouseId: activeWarehouseId, moduleScope: "restaurant" };
  const scopeKey = JSON.stringify([user?.id, activeCompanyId, activeWarehouseId, canView, isRestaurantCompany]);
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const loadMenu = useCallback(async (signal: AbortSignal) => {
    const data = await getRestaurantDigitalMenu({
      company_id: activeCompanyId, warehouse_id: activeWarehouseId,
      include_tables: "true", include_inactive: "true",
    }, { signal });
    if (!data || !Array.isArray(data.tables) || !("menu" in data)) {
      throw new Error("La configuration du menu est indisponible.");
    }
    if (data.menu && (Number(data.menu.warehouse_id) !== activeWarehouseId ||
      (data.menu.company_id && Number(data.menu.company_id) !== activeCompanyId))) {
      throw new Error("Ce menu n'appartient pas à l'établissement sélectionné.");
    }
    if (data.tables.some((table: RestaurantTable) => Number(table.warehouse_id) !== activeWarehouseId)) {
      throw new Error("Les tables ne correspondent pas à l'établissement sélectionné.");
    }
    return data as { menu: RestaurantMenu | null; tables: RestaurantTable[] };
  }, [activeCompanyId, activeWarehouseId]);
  const menuResource = useBusinessResource({ ...scope, resource: "restaurant-digital-menu",
    enabled: canView && isRestaurantCompany, active: true, load: loadMenu });
  const menu = menuResource.data?.menu || null;
  const tables = useMemo(() => menuResource.data?.tables || [], [menuResource.data]);
  const loading = menuResource.loading;
  const formReady = formScope === scopeKey && Boolean(menuResource.data);
  const loadDigitalMenu = () => { void menuResource.refresh().catch(() => {}); };
  const invalidateMenu = () => Promise.all([
    invalidateBusinessResources(queryClient, scope, "restaurant-digital-menu"),
    invalidateBusinessResources(queryClient, scope, "restaurant-digital-menu-preview"),
  ]);
  const publicUrl = menu?.menu_slug
    ? `${buildPublicBaseUrl()}/menu/${menu.menu_slug}`
    : "";

  const menuEnabled = Boolean(menu?.is_active);
  const orderEnabled = Boolean(menu?.allow_self_service_orders);

  useEffect(() => {
    const hadValues = Boolean(baseline.current);
    dirtyFields.current.clear();
    baseline.current = null;
    setFormScope("");
    setHasChanges(false);
    setRemoteChanged(false);
    setSaving(false);
    setTableSaving(false);
    setQrPrinting(false);
    setTableModalOpen(false);
    setQrPrintModalOpen(false);
    setEditingTable(null);
    setTableConflict(false);
    setSelectedQrPrintKeys([]);
    setActiveTab("presentation");
    if (hadValues) form.resetFields();
  }, [scopeKey, form]);

  useEffect(() => {
    if (!menuResource.data) return;
    const values = menuFormValues(menuResource.data.menu, user?.company?.name);
    if (!baseline.current || dirtyFields.current.size === 0) {
      form.setFieldsValue(values);
      baseline.current = values;
      setFormScope(scopeKey);
      setRemoteChanged(false);
    } else if (JSON.stringify(values) !== JSON.stringify(baseline.current)) {
      setRemoteChanged(true);
    }
  }, [menuResource.data, form, scopeKey, user?.company?.name]);

  const resolveRemoteChanges = (keepDraft: boolean) => {
    const fresh = menuFormValues(menu, user?.company?.name);
    const current = form.getFieldsValue(true);
    const edits = keepDraft ? Object.fromEntries(Array.from(dirtyFields.current).map((key) => [key, current[key]])) : {};
    form.setFieldsValue({ ...fresh, ...edits });
    baseline.current = fresh;
    if (!keepDraft) dirtyFields.current.clear();
    setHasChanges(keepDraft && dirtyFields.current.size > 0);
    setRemoteChanged(false);
  };

  useEffect(() => {
    let cancelled = false;
    setMenuQr(null);
    if (!publicUrl) {
      return;
    }

    generateQrDataUrl(publicUrl)
      .then((qr: string) => { if (!cancelled) setMenuQr(qr); })
      .catch(() => { if (!cancelled) setMenuQr(null); });
    return () => { cancelled = true; };
  }, [publicUrl]);

  useEffect(() => {
    if (!publicUrl || !tables.length) {
      setTableQrMap({});
      return;
    }

    let cancelled = false;
    Promise.all(
      tables.map(async (table) => {
        const url = `${publicUrl}?t=${encodeURIComponent(table.public_token)}`;
        const qr = await generateQrDataUrl(url);
        return [table.id, qr] as const;
      })
    )
      .then((entries) => {
        if (!cancelled) {
          setTableQrMap(Object.fromEntries(entries));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTableQrMap({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [publicUrl, tables]);

  const handleMenuNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (menu?.id) return;
    dirtyFields.current.add("menu_slug");
    form.setFieldValue("menu_slug", slugify(event.target.value));
  };

  const handleSaveMenu = async (values: MenuFormValues) => {
    if (!canEdit || writeInProgress.current || !formReady || remoteChanged) return;
    if (!activeCompanyId || !activeWarehouseId) {
      message.warning("Selectionnez d'abord une entreprise et un établissement.");
      return;
    }

    const startedScope = scopeKey;
    writeInProgress.current = true;
    setSaving(true);
    try {
      // Les onglets non montés gardent leurs valeurs dans le même formulaire.
      const allValues = { ...form.getFieldsValue(true), ...values } as MenuFormValues;
      if (!allValues.menu_name?.trim()) {
        setActiveTab("presentation");
        form.setFields([{ name: "menu_name", errors: ["Saisissez le nom du menu."] }]);
        return;
      }
      const colorField = (["primary_color", "accent_color", "secondary_color"] as const)
        .find((field) => allValues[field] && !/^#[0-9a-f]{6}$/i.test(allValues[field]!));
      if (colorField) {
        setActiveTab("appearance");
        form.setFields([{ name: colorField, errors: ["Utilisez un code couleur à six chiffres."] }]);
        return;
      }
      const fresh = await menuResource.refresh();
      if (currentScope.current !== startedScope) return;
      if (JSON.stringify(menuFormValues(fresh.menu, user?.company?.name)) !== JSON.stringify(baseline.current)) {
        setRemoteChanged(true);
        return;
      }
      values = allValues;
      const payload = {
        company_id: activeCompanyId,
        warehouse_id: activeWarehouseId,
        menu_name: values.menu_name,
        menu_slug: slugify(values.menu_slug || values.menu_name),
        description: values.description,
        logo_url: values.logo_url,
        is_active: Boolean(values.is_active),
        allow_self_service_orders: Boolean(values.allow_self_service_orders),
        auto_send_to_kitchen: Boolean(values.auto_send_to_kitchen),
        require_table_token: Boolean(values.require_table_token),
        require_customer_phone: Boolean(values.require_customer_phone),
        default_service_mode: values.default_service_mode || "dine_in",
        theme_config: {
          primary_color: values.primary_color || DEFAULT_THEME.primary_color,
          accent_color: values.accent_color || DEFAULT_THEME.accent_color,
          secondary_color:
            values.secondary_color || DEFAULT_THEME.secondary_color,
        },
      };

      const data = await saveRestaurantDigitalMenu(payload);
      if (currentScope.current !== startedScope) return;
      dirtyFields.current.clear();
      baseline.current = menuFormValues(data.menu, user?.company?.name);
      form.setFieldsValue(baseline.current);
      setHasChanges(false);
      setRemoteChanged(false);
      message.success("Menu digital enregistré.");
      await invalidateMenu();
    } catch (error: any) {
      if (currentScope.current === startedScope) message.error(
        error?.details || error?.message || "Erreur lors de l'enregistrement."
      );
    } finally {
      writeInProgress.current = false;
      if (currentScope.current === startedScope) setSaving(false);
    }
  };

  const openTableModal = (table?: RestaurantTable) => {
    if (!canEdit) return;
    setTableConflict(false);
    setEditingTable(table || null);
    tableForm.setFieldsValue({
      label: table?.label || "",
      seats: table?.seats || undefined,
      display_order: table?.display_order || 0,
      notes: table?.notes || "",
      is_active: table?.is_active ?? true,
    });
    setTableModalOpen(true);
  };

  const handleSaveTable = async (values: TableFormValues) => {
    if (!canEdit || writeInProgress.current || !activeCompanyId || !activeWarehouseId) return;
    const startedScope = scopeKey;
    writeInProgress.current = true;
    setTableSaving(true);
    try {
      const fresh = await menuResource.refresh();
      if (currentScope.current !== startedScope) return;
      if (editingTable) {
        const current = fresh.tables.find((table) => table.id === editingTable.id);
        if (!current || tableSnapshot(current) !== tableSnapshot(editingTable)) {
          setTableConflict(true);
          return;
        }
      }
      if (editingTable) {
        await updateRestaurantDigitalMenuTable(editingTable.id, {
          ...values,
          company_id: activeCompanyId,
        });
      } else {
        await createRestaurantDigitalMenuTable({
          ...values,
          company_id: activeCompanyId,
          warehouse_id: activeWarehouseId,
        });
      }
      if (currentScope.current !== startedScope) return;
      message.success(editingTable ? "Table mise à jour." : "Table créée.");
      setTableModalOpen(false);
      await invalidateMenu();
    } catch (error: any) {
      if (currentScope.current === startedScope) message.error(
        error?.details || error?.message || "Erreur lors de l'enregistrement."
      );
    } finally {
      writeInProgress.current = false;
      if (currentScope.current === startedScope) setTableSaving(false);
    }
  };

  const handleDeactivateTable = async (table: RestaurantTable) => {
    if (!canEdit || writeInProgress.current) return;
    if (!activeCompanyId) {
      message.warning("Selectionnez d'abord une entreprise.");
      return;
    }

    const startedScope = scopeKey;
    writeInProgress.current = true;
    try {
      const fresh = await menuResource.refresh();
      if (currentScope.current !== startedScope) return;
      const current = fresh.tables.find((row) => row.id === table.id);
      if (!current || !current.is_active || tableSnapshot(current) !== tableSnapshot(table)) {
        message.warning("Cette table a changé. Vérifiez son état avant de la désactiver.");
        return;
      }
      await deactivateRestaurantDigitalMenuTable(table.id, {
        company_id: activeCompanyId,
      });
      if (currentScope.current !== startedScope) return;
      message.success("Table désactivée.");
      await invalidateMenu();
    } catch (error: any) {
      if (currentScope.current === startedScope) message.error(
        error?.details || error?.message || "Erreur lors de la desactivation."
      );
    } finally {
      writeInProgress.current = false;
    }
  };

  const buildTableUrl = (table: RestaurantTable) =>
    `${publicUrl}?t=${encodeURIComponent(table.public_token)}`;

  const activeTables = useMemo(
    () => tables.filter((table) => table.is_active),
    [tables]
  );
  const requiresQrWithoutTables = Boolean(
    watchedAllowOrders && watchedRequireTableToken && activeTables.length === 0
  );
  const requiresQrWithNonDineInDefault = Boolean(
    watchedAllowOrders &&
      watchedRequireTableToken &&
      watchedDefaultServiceMode &&
      watchedDefaultServiceMode !== "dine_in"
  );

  const qrPrintOptions = useMemo(
    () => [
      {
        label: "Menu general",
        value: "menu",
      },
      ...activeTables.map((table) => ({
        label: table.label,
        value: `table:${table.id}`,
      })),
    ],
    [activeTables]
  );

  const allQrPrintKeys = useMemo(
    () => qrPrintOptions.map((option) => option.value),
    [qrPrintOptions]
  );

  const openQrPrintModal = () => {
    if (!publicUrl || !menu) {
      message.warning("Enregistrez le menu avant d'imprimer les QR.");
      return;
    }

    setSelectedQrPrintKeys((current) => {
      const validCurrent = current.filter((key) => allQrPrintKeys.includes(key));
      return validCurrent.length ? validCurrent : allQrPrintKeys;
    });
    setQrPrintModalOpen(true);
  };

  const getPrintLayout = (count: number) => {
    if (count <= 1) return { columns: 1, rows: 1 };
    if (count === 2) return { columns: 1, rows: 2 };
    if (count === 3) return { columns: 1, rows: 3 };
    if (count <= 4) return { columns: 2, rows: 2 };
    if (count <= 6) return { columns: 2, rows: 3 };
    if (count <= 9) return { columns: 3, rows: 3 };
    return { columns: 3, rows: Math.ceil(count / 3) };
  };

  const getQrSize = (count: number, columns: number, rows: number) => {
    const contentWidth = 190;
    const contentHeight = 250;
    const headerHeight = 20;
    const gap = 6;
    const cellWidth = (contentWidth - gap * Math.max(columns - 1, 0)) / columns;
    const cellHeight =
      (contentHeight - headerHeight - gap * Math.max(rows - 1, 0)) / rows;

    return Math.max(
      32,
      Math.min(cellWidth * 0.72, cellHeight * 0.68, count === 1 ? 136 : 96)
    );
  };

  const printQrSheet = async () => {
    if (!publicUrl || !menu) {
      message.warning("Enregistrez le menu avant d'imprimer les QR.");
      return;
    }

    const selectedKeys = selectedQrPrintKeys.filter((key) =>
      allQrPrintKeys.includes(key)
    );
    if (!selectedKeys.length) {
      message.warning("Selectionnez au moins un QR a imprimer.");
      return;
    }

    const startedScope = scopeKey;
    setQrPrinting(true);

    try {
      const menuQrData = menuQr || (await generateQrDataUrl(publicUrl));
      const tableEntries = await Promise.all(
        activeTables
          .filter((table) => selectedKeys.includes(`table:${table.id}`))
          .map(async (table) => {
            const url = buildTableUrl(table);
            const qr = tableQrMap[table.id] || (await generateQrDataUrl(url));
            return { table, url, qr };
          })
      );
      if (currentScope.current !== startedScope) return;

      const qrCards: QrPrintCard[] = [
        ...(selectedKeys.includes("menu")
          ? [
              {
                key: "menu",
                title: "Menu general",
                subtitle: menu.menu_name || "Menu digital",
                url: publicUrl,
                qr: menuQrData,
              },
            ]
          : []),
        ...tableEntries.map((entry) => ({
          key: `table:${entry.table.id}`,
          title: entry.table.label,
          subtitle: menu.menu_name || "Menu digital",
          url: entry.url,
          qr: entry.qr,
        })),
      ];

      const cardsHtml = qrCards
        .sort(
          (a, b) => selectedKeys.indexOf(a.key) - selectedKeys.indexOf(b.key)
        )
        .map(
          (card) => `
          <article class="qr-card">
            <div class="qr-title">${escapeHtml(card.title)}</div>
            <div class="qr-subtitle">${escapeHtml(card.subtitle)}</div>
            <img src="${card.qr}" alt="QR ${escapeHtml(card.title)}" />
            <div class="qr-url">${escapeHtml(card.url)}</div>
          </article>
        `
        )
        .join("");

      const selectedCount = selectedKeys.length;
      const layout = getPrintLayout(selectedCount);
      const qrSize = getQrSize(selectedCount, layout.columns, layout.rows);

      const printWindow = window.open("", "_blank", "width=1024,height=768");
      if (!printWindow) {
        message.error("La fenetre d'impression a ete bloquee.");
        return;
      }

      printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>QR menu digital</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 10mm;
              color: #142033;
              font-family: Arial, sans-serif;
              background: #fff;
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            @page { size: A4 portrait; margin: 10mm; }
            .sheet {
              min-height: 277mm;
              display: flex;
              flex-direction: column;
            }
            h1 { margin: 0 0 4px; font-size: 22px; }
            .sheet-subtitle { margin-bottom: 8mm; color: #64748b; font-size: 13px; }
            .grid {
              flex: 1;
              display: grid;
              grid-template-columns: repeat(${layout.columns}, minmax(0, 1fr));
              grid-template-rows: repeat(${layout.rows}, minmax(0, 1fr));
              gap: 6mm;
            }
            .qr-card {
              min-height: 0;
              padding: ${selectedCount <= 2 ? "9mm" : "6mm"};
              border: 1px solid #d9e2ec;
              border-radius: 8px;
              text-align: center;
              page-break-inside: avoid;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            .qr-title { font-size: ${selectedCount <= 2 ? 24 : 18}px; font-weight: 700; }
            .qr-subtitle { margin-top: 4px; color: #52637a; font-size: 13px; }
            img {
              width: ${qrSize}mm;
              height: ${qrSize}mm;
              margin: ${selectedCount <= 2 ? "8mm" : "5mm"} auto;
              display: block;
            }
            .qr-url { color: #475569; font-size: 10px; line-height: 1.35; word-break: break-all; }
            .powered-by {
              margin-top: 5mm;
              padding-top: 3mm;
              border-top: 1px solid #e2e8f0;
              color: #7a8699;
              font-size: 9px;
              line-height: 1.35;
              text-align: center;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <main class="sheet">
            <h1>${escapeHtml(menu.menu_name || "Menu digital")}</h1>
            <div class="sheet-subtitle">${escapeHtml(
              selectedCount === 1
                ? "1 QR code pret a imprimer."
                : `${selectedCount} QR codes prets a imprimer.`
            )}</div>
            <section class="grid">${cardsHtml}</section>
            <footer class="powered-by">${escapeHtml(QR_PRINT_SIGNATURE)}</footer>
          </main>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `);
      printWindow.document.close();
      setQrPrintModalOpen(false);
    } catch (error) {
      if (currentScope.current === startedScope) message.error("Impossible de préparer les QR. Réessayez.");
    } finally {
      if (currentScope.current === startedScope) setQrPrinting(false);
    }
  };

  const columns = [
    {
      title: "Table",
      dataIndex: "label",
      key: "label",
      render: (_: unknown, table: RestaurantTable) => (
        <Space direction="vertical" size={2}>
          <Text strong>{table.label}</Text>
          <Text type="secondary">
            {table.seats ? `${table.seats} places` : "Capacite non renseignee"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Lien QR",
      key: "qr",
      render: (_: unknown, table: RestaurantTable) => (
        <Space size={12} align="center">
          {tableQrMap[table.id] ? (
            <img
              className="restaurant-digital-menu__table-qr"
              src={tableQrMap[table.id]}
              alt={`QR ${table.label}`}
            />
          ) : (
            <QrcodeOutlined className="restaurant-digital-menu__table-qr-icon" />
          )}
          <Button
            icon={<CopyOutlined />}
            onClick={() =>
              copyToClipboard(buildTableUrl(table), "Lien de table copié.", message)
            }
            disabled={!publicUrl}
          >
            Copier
          </Button>
        </Space>
      ),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: boolean) =>
        value ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 72,
      align: "center" as const,
      render: (_: unknown, table: RestaurantTable) => {
        const deactivateDisabled = !table.is_active || !canEdit;

        return (
          <RowActionsMenu
            items={[
              {
                key: "edit",
                icon: <EditOutlined />,
                label: canEdit ? "Modifier" : "Modification indisponible",
                disabled: !canEdit,
                onClick: () => openTableModal(table),
              },
              { type: "divider" },
              {
                key: "deactivate",
                icon: <DeleteOutlined />,
                label: !table.is_active
                  ? "Table déjà désactivée"
                  : canEdit
                  ? "Désactiver"
                  : "Désactivation indisponible",
                disabled: deactivateDisabled,
                danger: !deactivateDisabled,
                onClick: () =>
                  modal.confirm({
                    title: "Désactiver cette table ?",
                    okText: "Désactiver",
                    cancelText: "Annuler",
                    okButtonProps: { danger: true },
                    onOk: () => handleDeactivateTable(table),
                  }),
              },
            ]}
            ariaLabel={`Afficher les actions de la table ${table.label || table.id}`}
          />
        );
      },
    },
  ];

  if (!isRestaurantCompany) {
    return (
      <Card>
        <Empty
          description="Le menu digital est disponible uniquement pour les entreprises configurees avec le profil Restauration."
        />
      </Card>
    );
  }

  if (!canView) {
    return (
      <Card>
        <Empty description="Vous n'avez pas la permission de consulter le menu digital." />
      </Card>
    );
  }

  if (!activeCompanyId || !activeWarehouseId) {
    return (
      <Card>
        <Alert
          type="warning"
          showIcon
          message="Aucun établissement actif selectionne"
          description="Selectionnez un établissement pour configurer le menu digital."
        />
      </Card>
    );
  }

  const cannotSave = !canEdit ? "Vous disposez d’un accès en lecture seule."
    : remoteChanged ? "Vérifiez les réglages modifiés depuis une autre session."
    : menuResource.error ? "Actualisez la configuration avant d’enregistrer."
    : !formReady ? "La configuration doit être chargée avant l’enregistrement." : "";
  const sharingUnavailable = !publicUrl ? "Enregistrez le menu pour générer son lien et ses QR."
    : menuResource.error ? "Actualisez les données avant d’imprimer les QR." : "";
  const settings = [
    { name: "allow_self_service_orders", label: "Autoriser les commandes", help: "Les clients peuvent commander depuis le menu public." },
    { name: "auto_send_to_kitchen", label: "Envoi automatique en cuisine", help: "Transmettre directement les commandes reçues à la cuisine." },
    { name: "require_customer_phone", label: "Téléphone obligatoire", help: "Demander un numéro de téléphone lors de la commande." },
    { name: "require_table_token", label: "QR de table requis", help: "Un QR de table valide est nécessaire pour passer commande." },
  ];
  const sectionHead = (icon: React.ReactNode, title: string, help: string) => <div className="restaurant-digital-menu__section-head">
    <div className="restaurant-digital-menu__section-title">{icon}<div><h2>{title}</h2><p>{help}</p></div></div>
  </div>;

  return (
    <div className="restaurant-digital-menu">
      {messageContextHolder}{modalContextHolder}
      <section className="restaurant-digital-menu__hero">
        <div>
          <div className="restaurant-digital-menu__title-row"><Title level={2}>Menu digital</Title>
            {menuEnabled ? <Tag icon={<CheckCircleOutlined />} color="green">Publié</Tag> : <Tag>Non publié</Tag>}
            {orderEnabled ? <Tag>Commandes activées</Tag> : <Tag>Consultation seule</Tag>}
          </div>
          <Text type="secondary">Personnalisez votre menu et vérifiez le rendu côté client.</Text>
        </div>
        <Button aria-label="Actualiser" icon={<ReloadOutlined />} onClick={loadDigitalMenu} loading={loading}>Actualiser</Button>
      </section>

      <BusinessResourceNotice resource={menuResource} label="la configuration du menu" />
      {remoteChanged && <Alert type="warning" showIcon message="Les réglages ont changé depuis une autre session"
        description="Votre saisie est conservée. Choisissez les valeurs à reprendre avant d’enregistrer."
        action={<Space wrap><Button onClick={() => resolveRemoteChanges(true)}>Conserver ma saisie</Button>
          <Button onClick={() => resolveRemoteChanges(false)}>Recharger les réglages</Button></Space>} />}
      <Tabs activeKey={activeTab} onChange={setActiveTab} className="restaurant-digital-menu__tabs" items={[
        { key: "presentation", label: <><FileTextOutlined /> Présentation</> },
        { key: "commands", label: <><SettingOutlined /> Commandes</> },
        { key: "appearance", label: <><PictureOutlined /> Apparence</> },
        { key: "tables", label: <><AppstoreOutlined /> Tables et QR · {tables.length}</> },
      ]} />
      {!formReady ? (!menuResource.error && <div className="restaurant-digital-menu__loading"><Spin /><Text>Chargement de la configuration…</Text></div>) : <>
        <div className="restaurant-digital-menu__workspace">
          <div className="restaurant-digital-menu__editor" role="tabpanel" aria-label={{ presentation: "Présentation", commands: "Commandes", appearance: "Apparence", tables: "Tables et QR" }[activeTab]}>
            <Form form={form} layout="vertical" onFinish={handleSaveMenu} disabled={!canEdit || saving}
              onValuesChange={(changed) => { Object.keys(changed).forEach((key) => dirtyFields.current.add(key)); setHasChanges(true); }}
              onFinishFailed={() => setActiveTab("presentation")}>
              {activeTab === "presentation" && <section className="restaurant-digital-menu__section">
                {sectionHead(<FileTextOutlined />, "Identité du menu", "Informations principales visibles par les clients.")}
                <div className="restaurant-digital-menu__identity-grid">
                  <Form.Item name="menu_name" label="Nom du menu" rules={[{ required: true, whitespace: true, message: "Saisissez le nom du menu." }]}>
                    <Input onChange={handleMenuNameChange} />
                  </Form.Item>
                  <Form.Item name="menu_slug" label="Adresse publique (lien personnalisé)" extra={publicUrl || "Le lien sera disponible après l’enregistrement."}>
                    <Input prefix={<LinkOutlined />} />
                  </Form.Item>
                </div>
                <Form.Item name="description" label="Description"><TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder="Décrivez brièvement votre menu (facultatif)…" /></Form.Item>
                <Form.Item name="logo_url" label="Logo ou image d’en-tête"><Input prefix={<PictureOutlined />} placeholder="https://…" /></Form.Item>
                <div className="restaurant-digital-menu__setting-row">
                  <Form.Item name="is_active" valuePropName="checked" noStyle><Switch aria-label="Publier le menu" /></Form.Item>
                  <div><strong>Publier le menu</strong><p>Rendre le menu visible via le lien public et les QR.</p></div>
                </div>
              </section>}

              {activeTab === "commands" && <section className="restaurant-digital-menu__section">
                {sectionHead(<SettingOutlined />, "Commandes et service", "Configurez la prise de commande et le mode de service du menu public.")}
                {settings.map((setting) => <div className="restaurant-digital-menu__setting-row restaurant-digital-menu__setting-row--spread" key={setting.name}>
                  <div><label htmlFor={`menu-${setting.name}`}>{setting.label}</label><p>{setting.help}</p></div>
                  <Form.Item name={setting.name} valuePropName="checked" noStyle><Switch id={`menu-${setting.name}`} aria-label={setting.label} /></Form.Item>
                </div>)}
                <Form.Item name="default_service_mode" label="Mode de service par défaut" className="restaurant-digital-menu__service-field"><Select options={SERVICE_MODE_OPTIONS} /></Form.Item>
              </section>}
              {(activeTab === "presentation" || activeTab === "appearance") && <section className="restaurant-digital-menu__section">
                {sectionHead(<PictureOutlined />, "Apparence du menu", "Personnalisez les couleurs de votre menu public.")}
                <div className="restaurant-digital-menu__colors">{[
                  ["primary_color", "Couleur principale"], ["accent_color", "Couleur d’accent"], ["secondary_color", "Fond secondaire"],
                ].map(([name, label]) => <Form.Item name={name} label={label} key={name}
                  rules={[{ pattern: /^#[0-9a-f]{6}$/i, message: "Utilisez un code couleur à six chiffres, par exemple #173f5f." }]}>
                  <MenuColorInput label={label} /></Form.Item>)}</div>
                {activeTab === "appearance" && <p className="restaurant-digital-menu__help">Les couleurs choisies concernent le menu public. L’espace de gestion suit le thème ELSA GESTION.</p>}
              </section>}
            </Form>
            {(requiresQrWithoutTables || requiresQrWithNonDineInDefault) && <div className="restaurant-digital-menu__alerts">
              {requiresQrWithoutTables && <Alert type="warning" showIcon message="QR de table requis sans table active"
                description="Ajoutez une table active ou désactivez l’exigence de QR pour permettre les commandes depuis le lien général."
                action={<Button onClick={() => setActiveTab("tables")}>Voir les tables</Button>} />}
              {requiresQrWithNonDineInDefault && <Alert type="info" showIcon message="Le QR de table force le mode sur place"
                description="Avec un QR de table valide, la commande est rattachée à la table et traitée sur place, même si le mode par défaut est différent." />}
            </div>}
            {(activeTab === "presentation" || activeTab === "tables") && <section className="restaurant-digital-menu__section">
              <div className="restaurant-digital-menu__section-head restaurant-digital-menu__section-head--table">
                <div className="restaurant-digital-menu__section-title"><AppstoreOutlined /><div><h2>Tables et QR</h2><p>Un lien dédié à chaque table pour commander sur place.</p></div></div>
                <Button aria-label="Ajouter une table" icon={<PlusOutlined />} onClick={() => openTableModal()} disabled={!canEdit || Boolean(menuResource.error) || saving}>Ajouter une table</Button>
              </div>
              {!canEdit && <p className="restaurant-digital-menu__help">La gestion des tables nécessite la permission de modification.</p>}
              <Table rowKey="id" columns={columns} dataSource={tables} pagination={false} size="small"
                scroll={{ x: 520 }} locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune table configurée" /> }} />
            </section>}
          </div>
          <aside className="restaurant-digital-menu__right-column">
            <RestaurantDigitalMenuPreview key={scopeKey} scope={scope} values={draftValues || menuFormValues(menu)}
              menuSlug={menu?.menu_slug} published={menuEnabled} enabled={canView && isRestaurantCompany && formReady} publicUrl={publicUrl} />
            <section className="restaurant-digital-menu__sharing" aria-label="Lien et QR du menu enregistré">
              {menuQr ? <img className="restaurant-digital-menu__main-qr" src={menuQr} alt="QR principal du menu enregistré" /> : <QrcodeOutlined className="restaurant-digital-menu__qr-placeholder" />}
              <div><h2>Lien du menu public</h2><Input value={publicUrl} readOnly aria-label="Lien du menu public enregistré" />
                <div className="restaurant-digital-menu__sharing-actions"><Button aria-label="Copier le lien" icon={<CopyOutlined />} onClick={() => copyToClipboard(publicUrl, "Lien du menu copié.", message)} disabled={!publicUrl}>Copier le lien</Button>
                  <Button aria-label="Imprimer les QR" icon={<PrinterOutlined />} onClick={openQrPrintModal} disabled={Boolean(sharingUnavailable) || loading}>Imprimer les QR</Button></div>
                {sharingUnavailable && <p className="restaurant-digital-menu__help">{sharingUnavailable}</p>}
                {hasChanges && <p className="restaurant-digital-menu__help">Le lien et les QR utilisent les derniers réglages enregistrés.</p>}
              </div>
            </section>
          </aside>
        </div>
        <footer className="restaurant-digital-menu__footer">
          <Button icon={<GlobalOutlined />} href={publicUrl || undefined} target="_blank" rel="noopener noreferrer" disabled={!publicUrl}>Ouvrir le menu public</Button>
          <div className="restaurant-digital-menu__save"><span className="restaurant-digital-menu__help">{cannotSave || (hasChanges ? "Modifications non enregistrées" : "Configuration enregistrée")}</span>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={Boolean(cannotSave) || loading} onClick={() => form.submit()}>Enregistrer les modifications</Button></div>
        </footer>
      </>}

      <Modal
        title="Selection des QR a imprimer"
        open={qrPrintModalOpen}
        onCancel={() => setQrPrintModalOpen(false)}
        onOk={printQrSheet}
        okText="Imprimer la selection"
        confirmLoading={qrPrinting}
        okButtonProps={{ disabled: selectedQrPrintKeys.length === 0 }}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="La feuille s'adapte au nombre de QR selectionnes"
            description="Un seul QR occupe toute la page. Deux QR divisent la page en deux zones. Les selections plus larges sont reparties automatiquement pour utiliser l'espace disponible."
          />
          <Space wrap>
            <Button
              size="small"
              onClick={() => setSelectedQrPrintKeys(allQrPrintKeys)}
            >
              Tout selectionner
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedQrPrintKeys(["menu"])}
            >
              Menu general
            </Button>
            <Button
              size="small"
              onClick={() =>
                setSelectedQrPrintKeys(
                  activeTables.map((table) => `table:${table.id}`)
                )
              }
              disabled={!activeTables.length}
            >
              Tables actives
            </Button>
          </Space>
          <Checkbox.Group
            className="restaurant-digital-menu__qr-print-list"
            options={qrPrintOptions}
            value={selectedQrPrintKeys}
            onChange={(values) =>
              setSelectedQrPrintKeys(values.map((value) => String(value)))
            }
          />
          <Text type="secondary">
            {selectedQrPrintKeys.length} QR selectionne
            {selectedQrPrintKeys.length > 1 ? "s" : ""}.
          </Text>
        </Space>
      </Modal>

      <Modal
        title={editingTable ? "Modifier la table" : "Ajouter une table"}
        open={tableModalOpen}
        onCancel={() => setTableModalOpen(false)}
        onOk={() => tableForm.submit()}
        okText="Enregistrer"
        confirmLoading={tableSaving}
        okButtonProps={{ disabled: !canEdit || tableConflict }}
        forceRender
      >
        {tableConflict && <Alert type="warning" showIcon message="Cette table a changé depuis une autre session"
          description="Votre saisie est conservée. Rechargez la table avant de reprendre la modification."
          action={<Button disabled={!tables.some((table) => table.id === editingTable?.id)} onClick={() => {
            const current = tables.find((table) => table.id === editingTable?.id);
            if (current) openTableModal(current);
          }}>Recharger la table</Button>} style={{ marginBottom: 16 }} />}
        <Form
          name="restaurant-digital-menu-table"
          form={tableForm}
          layout="vertical"
          onFinish={handleSaveTable}
          initialValues={{ is_active: true, display_order: 0 }}
          disabled={!canEdit || tableSaving}
        >
          <Form.Item
            name="label"
            label="Libelle"
            rules={[{ required: true, message: "Libelle requis" }]}
          >
            <Input placeholder="Table 1, Terrasse 4..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="seats" label="Places">
                <InputNumber min={1} max={99} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="display_order" label="Ordre">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes internes">
            <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RestaurantDigitalMenu;
